import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

export default function AdminRecoverConsume() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setError("Missing recovery token in URL.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/recovery/consume", { token });
        setToken(data.token);
        await loginWithToken(data.token);
        setStatus("success");
        toast.warning("2FA was disabled. Please re-enable it now.");
        setTimeout(() => navigate("/admin/profile", { replace: true }), 1200);
      } catch (err) {
        setStatus("error");
        setError(formatApiError(err));
      }
    })();
  }, [params, loginWithToken, navigate]);

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center px-6"
      data-testid="recover-consume-page"
    >
      <div className="max-w-md w-full text-center border border-border rounded-2xl bg-card p-8">
        {status === "processing" && (
          <div className="space-y-3" data-testid="recover-consume-processing">
            <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
            <p className="text-muted-foreground">Verifying recovery token…</p>
          </div>
        )}
        {status === "success" && (
          <div className="space-y-3" data-testid="recover-consume-success">
            <ShieldCheck className="w-10 h-10 mx-auto text-primary" />
            <h2 className="font-display text-2xl">Access restored</h2>
            <p className="text-sm text-muted-foreground">
              2FA has been disabled on your account. Redirecting to your profile —
              please re-enable 2FA immediately.
            </p>
          </div>
        )}
        {status === "error" && (
          <div className="space-y-3" data-testid="recover-consume-error">
            <AlertTriangle className="w-10 h-10 mx-auto text-destructive" />
            <h2 className="font-display text-2xl">Recovery failed</h2>
            <p className="text-sm text-destructive">{error}</p>
            <a
              href="/admin/login"
              className="inline-block mt-2 text-primary hover:underline text-sm"
            >
              Back to sign in
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
